// RapportClient.jsx - Version Tailwind CSS natif
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { APP_CONFIG } from '../../config/appConfig';
import {
  Users, GraduationCap, FileQuestion, Award,
  BarChart3, TrendingUp, Clock, Calendar,
  Building2, UserCheck, Loader2, RefreshCw,
  Download, PieChart, Activity, AlertCircle
} from 'lucide-react';

const StatCard = ({ title, value, icon: IconComponent, color, subtitle, trend }) => (
  <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm hover:shadow-md transition">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-[9px] font-black text-slate-400 uppercase tracking-wider">{title}</p>
        <p className="text-xl font-black text-slate-800 mt-1">{value}</p>
        {subtitle && <p className="text-[9px] text-slate-400 mt-0.5">{subtitle}</p>}
        {trend && <span className={`text-[9px] font-bold ${trend > 0 ? 'text-green-600' : 'text-red-600'} mt-1 inline-block`}>{trend > 0 ? '↑' : '↓'} {Math.abs(trend)}%</span>}
      </div>
      <div className={`p-2.5 rounded-xl ${color}`}>{IconComponent && <IconComponent size={16} className="text-white" />}</div>
    </div>
  </div>
);

const ProgressBar = ({ label, value, color = 'bg-indigo-500', max = 100 }) => (
  <div className="space-y-1">
    <div className="flex justify-between text-[10px]">
      <span className="text-slate-500">{label}</span>
      <span className="font-bold text-slate-700">{value}%</span>
    </div>
    <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
      <div className={`h-full ${color} rounded-full transition-all duration-500`} style={{ width: `${Math.min(value, max)}%` }} />
    </div>
  </div>
);

export default function RapportClient() {
  const { token, user, isAuthenticated } = useAuth();
  const clientId = user?.clientId || user?.client?.id || user?.id;

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    etudiants: { total: 0, actifs: 0, inactifs: 0 },
    formations: { total: 0, parType: { PDF: 0, JSON: 0, EVALUATION: 0 } },
    evaluations: { total: 0, diagnostiques: 0, sommatives: 0, tauxReussite: 0 },
    inscriptions: { total: 0, enCours: 0, terminees: 0 },
    progression: { moyenne: 0, parFormation: [] },
    activites: []
  });

  const fetchStats = useCallback(async () => {
    if (!token || !clientId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${APP_CONFIG.BASE_URL}/api/v1/client/rapport/statistiques`,
        { headers: { "Authorization": `Bearer ${token}`, "Accept": "application/json" } }
      );
      if (!response.ok) throw new Error(`Erreur HTTP ${response.status}`);
      const data = await response.json();
      const statsData = data.data || data || {};
      setStats({
        etudiants: {
          total: statsData.totalEtudiants || 0,
          actifs: statsData.etudiantsActifs || 0,
          inactifs: (statsData.totalEtudiants || 0) - (statsData.etudiantsActifs || 0)
        },
        formations: {
          total: statsData.totalFormations || 0,
          parType: statsData.formationsParType || { PDF: 0, JSON: 0, EVALUATION: 0 }
        },
        evaluations: {
          total: statsData.totalEvaluations || 0,
          diagnostiques: statsData.evaluationsDiagnostiques || 0,
          sommatives: statsData.evaluationsSommatives || 0,
          tauxReussite: statsData.tauxReussite || 0
        },
        inscriptions: {
          total: statsData.totalInscriptions || 0,
          enCours: statsData.inscriptionsEnCours || 0,
          terminees: statsData.inscriptionsTerminees || 0
        },
        progression: {
          moyenne: statsData.progressionMoyenne || 0,
          parFormation: statsData.progressionParFormation || []
        },
        activites: statsData.activites || []
      });
    } catch (err) {
      console.error("Erreur récupération statistiques:", err);
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, clientId]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const exportReport = () => {
    const rows = [
      ['Rapport du Client', new Date().toLocaleDateString('fr-FR')],
      [],
      ['Métrique', 'Valeur'],
      ['Total Étudiants', stats.etudiants.total],
      ['Étudiants Actifs', stats.etudiants.actifs],
      ['Total Formations', stats.formations.total],
      ['Total Évaluations', stats.evaluations.total],
      ['Taux de Réussite', `${stats.evaluations.tauxReussite}%`],
      ['Total Inscriptions', stats.inscriptions.total],
      ['Inscriptions en Cours', stats.inscriptions.enCours],
      ['Progression Moyenne', `${stats.progression.moyenne}%`]
    ];
    const csvContent = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `rapport_client_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  if (!isAuthenticated || !token) {
    return (
      <div className="p-6 text-center">
        <div className="max-w-md mx-auto bg-amber-50 border border-amber-200 rounded-xl p-6">
          <AlertCircle size={24} className="mx-auto text-amber-500 mb-2" />
          <p className="text-gray-700 font-medium">Accès réservé aux clients</p>
          <p className="text-sm text-gray-500 mt-1">Veuillez vous connecter.</p>
        </div>
      </div>
    );
  }

  if (!clientId) {
    return (
      <div className="p-6 text-center">
        <div className="max-w-md mx-auto bg-red-50 border border-red-200 rounded-xl p-6">
          <AlertCircle size={24} className="mx-auto text-red-500 mb-2" />
          <p className="text-gray-700 font-medium">Erreur de configuration</p>
          <p className="text-sm text-gray-500 mt-1">Impossible de récupérer l'identifiant du client.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[60vh]">
        <Loader2 className="animate-spin text-indigo-600" size={40} />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 bg-slate-50 min-h-screen font-sans">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-black text-slate-800 uppercase tracking-tighter flex items-center gap-3">
            <BarChart3 size={28} className="text-indigo-600" />
            Rapport Client
          </h1>
          <p className="text-xs text-slate-500 mt-1">Vue d'ensemble de vos étudiants et formations</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={exportReport} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm flex items-center gap-2">
            <Download size={16} /> Exporter
          </button>
          <button onClick={() => { setRefreshing(true); fetchStats(); }} disabled={refreshing} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition text-sm flex items-center gap-2 disabled:opacity-50">
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} /> {refreshing ? 'Rafraîchissement...' : 'Rafraîchir'}
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{error}</div>}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title="Étudiants" value={stats.etudiants.total} icon={Users} color="bg-indigo-600" subtitle={`${stats.etudiants.actifs} actifs`} />
        <StatCard title="Formations" value={stats.formations.total} icon={GraduationCap} color="bg-emerald-600" />
        <StatCard title="Évaluations" value={stats.evaluations.total} icon={FileQuestion} color="bg-purple-600" subtitle={`${stats.evaluations.diagnostiques} diag / ${stats.evaluations.sommatives} som`} />
        <StatCard title="Taux Réussite" value={`${stats.evaluations.tauxReussite}%`} icon={Award} color="bg-amber-600" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
          <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2"><PieChart size={16} className="text-indigo-600" /> Répartition des formations</h3>
          <div className="space-y-2">
            <div className="flex justify-between text-xs"><span className="text-slate-500">PDF</span><span className="font-bold">{stats.formations.parType?.PDF || 0}</span></div>
            <div className="flex justify-between text-xs"><span className="text-slate-500">JSON</span><span className="font-bold">{stats.formations.parType?.JSON || 0}</span></div>
            <div className="flex justify-between text-xs"><span className="text-slate-500">Évaluations</span><span className="font-bold">{stats.formations.parType?.EVALUATION || 0}</span></div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
          <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2"><TrendingUp size={16} className="text-emerald-600" /> Progression</h3>
          <div className="space-y-3">
            <ProgressBar label="Progression moyenne" value={stats.progression.moyenne || 0} color="bg-blue-500" />
            <ProgressBar label="Inscriptions en cours" value={stats.inscriptions.total > 0 ? Math.round((stats.inscriptions.enCours / stats.inscriptions.total) * 100) : 0} color="bg-amber-500" />
            <ProgressBar label="Taux de réussite" value={stats.evaluations.tauxReussite || 0} color="bg-green-500" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
        <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2"><Calendar size={16} className="text-indigo-600" /> Inscriptions</h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-slate-50 p-3 rounded-xl text-center"><p className="text-[10px] text-slate-400">Total</p><p className="text-xl font-bold text-slate-800">{stats.inscriptions.total || 0}</p></div>
          <div className="bg-blue-50 p-3 rounded-xl text-center"><p className="text-[10px] text-slate-400">En cours</p><p className="text-xl font-bold text-blue-600">{stats.inscriptions.enCours || 0}</p></div>
          <div className="bg-green-50 p-3 rounded-xl text-center"><p className="text-[10px] text-slate-400">Terminées</p><p className="text-xl font-bold text-green-600">{stats.inscriptions.terminees || 0}</p></div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
        <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2"><Activity size={16} className="text-indigo-600" /> Activités récentes</h3>
        {stats.activites?.length === 0 ? <p className="text-xs text-slate-400 italic">Aucune activité récente</p> : (
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {stats.activites?.map((act, idx) => (
              <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg border border-slate-100">
                <span className="text-xs text-slate-700">{act.message}</span>
                <span className="text-[10px] text-slate-400">{act.date ? new Date(act.date).toLocaleDateString('fr-FR') : 'N/A'}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-slate-800 rounded-2xl p-3 text-center">
        <div className="flex items-center justify-center gap-4 text-[8px] text-slate-400 flex-wrap">
          <span className="flex items-center gap-1.5"><Users size={10} /> {stats.etudiants.total} étudiants</span>
          <span className="w-px h-4 bg-slate-700" />
          <span className="flex items-center gap-1.5"><GraduationCap size={10} /> {stats.formations.total} formations</span>
          <span className="w-px h-4 bg-slate-700" />
          <span className="flex items-center gap-1.5"><FileQuestion size={10} /> {stats.evaluations.total} évaluations</span>
          <span className="w-px h-4 bg-slate-700" />
          <span className="text-slate-500">{new Date().toLocaleString('fr-FR')}</span>
        </div>
      </div>
    </div>
  );
}